import React, { useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Dialog,
  DialogContent,
  useTheme,
  useMediaQuery,
  GlobalStyles,
} from "@mui/material";
import { ArrowBackIos, ArrowForwardIos, Close } from "@mui/icons-material";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";

const PhotoCarousel = ({ images }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const chunkSize = isMobile ? 1 : 3;

  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const handleOpen = (src) => {
    setSelectedImage(src);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImage(null);
  };

  const slides = useMemo(() => {
    // Calculate how many slides we will need based on image count and chunk size
    const totalSlides = Math.ceil(images.length / chunkSize);
    const slideChunks = Array.from({ length: totalSlides }, (_, i) => {
      const start = i * chunkSize;
      const end = start + chunkSize;
      const chunk = images.slice(start, end);
      return chunk;
    });

    return slideChunks;
  }, [images, chunkSize]);

  return (
    <>
      <Box
        sx={{
          marginTop: -11,
          position: "relative",
          paddingBottom: 3,
        }}
      >
        <GlobalStyles
          styles={{
            ".carousel .slide img": {
              width: "100% !important",
              height: "100% !important",
              objectFit: "cover",
              borderRadius: "0px",
              cursor: "pointer",
            },
            ".carousel.carousel-slider": {
              overflow: "visible !important",
            },
            ".carousel .control-dots": {
              bottom: "-40px",
              position: "absolute",
            },
          }}
        />
        <Carousel
          showStatus={false}
          showThumbs={false}
          infiniteLoop
          swipeable
          emulateTouch
          renderArrowPrev={(onClick, hasPrev, label) =>
            hasPrev && (
              <IconButton
                onClick={onClick}
                aria-label={label}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: 0,
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  color: "white",
                  marginLeft: "-50px",
                }}
              >
                <ArrowBackIos />
              </IconButton>
            )
          }
          renderArrowNext={(onClick, hasNext, label) =>
            hasNext && (
              <IconButton
                onClick={onClick}
                aria-label={label}
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: 0,
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  color: "white",
                  marginRight: "-50px",
                }}
              >
                <ArrowForwardIos />
              </IconButton>
            )
          }
          renderIndicator={(onClick, isSelected, index, label) => (
            <span
              key={index}
              onClick={onClick}
              style={{
                display: "inline-block",
                margin: "0 6px",
                width: 12,
                height: 12,
                borderRadius: 2,
                backgroundColor: isSelected
                  ? theme.palette.custom.white
                  : "transparent",
                border: "2px solid " + theme.palette.custom.white,
                cursor: "pointer",
              }}
              title={label}
            />
          )}
        >
          {slides.map((chunk, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                justifyContent:
                  isMobile || chunk.length < chunkSize
                    ? "center"
                    : "space-between",
                alignItems: "center",
                gap: 2,
                px: 2,
                py: 1,
                // height: { xs: 250, sm: 150, md: 200 },
                boxSizing: "border-box",
              }}
            >
              {chunk.map((src, j) => (
                <Box
                  key={j}
                  sx={{
                    position: "relative",
                    flex:
                      chunk.length < chunkSize
                        ? "0 0 calc((100% - 32px) / 3)"
                        : 1,
                    aspectRatio: "5 / 4",
                    cursor: "pointer",
                  }}
                  onClick={() => handleOpen(src)}
                >
                  <Box
                    component="img"
                    src={src}
                    alt={`slide-${i}-${j}`}
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              ))}
            </Box>
          ))}
        </Carousel>
      </Box>

      {/* Dialog for image preview */}
      <Dialog open={open} onClose={handleClose} maxWidth="lg">
        <DialogContent
          sx={{
            position: "relative", // so the close button can be absolutely positioned
            p: 0,
            backgroundColor: "black",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "white",
              zIndex: 1,
              backgroundColor: "rgba(0,0,0,0.5)",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.7)",
              },
            }}
          >
            <Close />
          </IconButton>

          {/* Preview image */}
          <Box
            component="img"
            src={selectedImage}
            alt="preview"
            sx={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PhotoCarousel;
